import type { CustomCellEditorProps } from "ag-grid-react";
import { useRef, useState } from "react";
import type { ActionMeta } from "react-select";
import WindowedSelect from "react-windowed-select";

import type { ISelectOption } from "@/app-utils/form.ts";
import { isBackspace, isPrintableKey } from "@/ui-modules/core/grid/cells/editors/utils.ts";

import type { CellEditorParams } from "../../cell-editor-factory.js";
import { TableCell } from "../core/TableCell.js";

export const SelectCellEditor = (props: CustomCellEditorProps<string> & CellEditorParams) => {
  const { value, selectOptions } = props;
  const isPrintableEditingKey = isPrintableKey(props.eventKey);
  const isBackspaceKey = isBackspace(props.eventKey);

  const initialSelection: ISelectOption = selectOptions.find((option) => option.label == value);

  const [tag, setTag] = useState<ISelectOption>(initialSelection);
  const refInput = useRef(null);

  const onChangeListener = (option: ISelectOption, actionMeta: ActionMeta<ISelectOption>) => {
    setTag(option);
    props.onValueChange(option?.label);
    if (actionMeta.action === "select-option" || actionMeta.action === "create-option") {
      setTimeout(props.stopEditing, 0);
    }
  };

  return (
    <TableCell editor>
      <WindowedSelect
        windowThreshold={50}
        ref={refInput}
        className="react-select"
        classNamePrefix={"react-select"}
        isMulti={false}
        openMenuOnFocus={true}
        isSearchable={true}
        closeMenuOnSelect={true}
        isClearable={true}
        value={tag}
        defaultInputValue={isPrintableEditingKey ? props.eventKey : "" /* initialOrgProperty?.value*/}
        getOptionValue={(option: ISelectOption) => option.value}
        options={selectOptions}
        onChange={onChangeListener}
        onFocus={(_event) => (isBackspaceKey ? setTag(null) : undefined)}
        autoFocus={true}
        //blurInputOnSelect={true} // this makes the stopEditing happen too early, therefore the previous tag is selected
        onBlur={() => props.stopEditing()}
        tabSelectsValue={false}
        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }} // do not show the caret
        maxMenuHeight={200}
        menuPortalTarget={document.body}
        onKeyDown={(e) => {
          // as explained in https://www.ag-grid.com/react-data-grid/component-cell-editor/
          // we should stop propagation of the Enter key down, as this is interpreted by ag-grid as edit stop
          // ...but this does not seem to work
          if (e.key === "Enter") {
            e.stopPropagation();
          }
        }}
        styles={{
          control: (base) => ({
            ...base,
            minHeight: 25, // resize the minHeight
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 650,
          }),
          menu: (base) => ({
            // let the menu extend its width to the longest option
            ...base,
            width: "max-content",
            minWidth: "100%",
          }),
        }}
      />
    </TableCell>
  );
};
